import { useMemo, useState } from 'react'
import { AlertTriangle, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils/cn'
import { validateInstance } from '../../lib/validate-import'
import { editKey } from '../../lib/apply-edits'
import type { BuiltRecord } from '../../lib/build-records'
import type {
  FieldDefinition,
  ConditionalRule,
} from '@/features/form-builder/api/form-builder-types'

export interface GridColumn {
  fieldKey: string
  label: string
}

function valueToText(v: unknown): string {
  if (v == null) return ''
  if (Array.isArray(v)) return v.join('،')
  if (typeof v === 'boolean') return v ? 'true' : 'false'
  return String(v)
}

interface FlatRow {
  recordIndex: number
  instanceIndex: number
  displayKey: string
  inst: BuiltRecord['instances'][number]
}

export function ValidationGrid({
  records,
  columns,
  fields,
  conditionalRules,
  edits,
  onEditCell,
}: {
  records: BuiltRecord[]
  columns: GridColumn[]
  fields: FieldDefinition[]
  conditionalRules: ConditionalRule[]
  edits: Record<string, string>
  onEditCell: (recordIndex: number, instanceIndex: number, fieldKey: string, raw: string) => void
}) {
  const [errorsOnly, setErrorsOnly] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)

  const flat = useMemo<FlatRow[]>(() => {
    const out: FlatRow[] = []
    records.forEach((rec, ri) => {
      rec.instances.forEach((inst, ii) => {
        out.push({ recordIndex: ri, instanceIndex: ii, displayKey: `${ri + 1}.${ii + 1}`, inst })
      })
    })
    return out
  }, [records])

  // Validate each flat row.
  const validations = useMemo(
    () => flat.map((r) => validateInstance(fields, conditionalRules, r.inst)),
    [flat, fields, conditionalRules],
  )

  const summary = useMemo(() => {
    let errors = 0
    let warnings = 0
    validations.forEach((v) => {
      if (v.hasBlocking) errors++
      if (v.hasWarnings) warnings++
    })
    return { total: flat.length, errors, warnings, clean: flat.length - errors }
  }, [validations, flat.length])

  const visibleIdx = flat
    .map((_, i) => i)
    .filter((i) => !errorsOnly || validations[i].hasBlocking)

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Stat icon={CheckCircle2} cls="text-emerald-500" label={`${summary.clean} معتبر`} />
        <Stat icon={AlertCircle} cls="text-destructive" label={`${summary.errors} خطا`} />
        <Stat icon={AlertTriangle} cls="text-yellow-500" label={`${summary.warnings} هشدار`} />
        <span className="text-muted-foreground">از مجموع {summary.total} نمونه</span>
        <label className="flex items-center gap-1.5 ms-auto text-xs cursor-pointer select-none">
          <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />
          فقط خطادارها
        </label>
      </div>

      {/* Grid */}
      <div className="overflow-auto rounded-lg border border-border max-h-[55vh]">
        <table className="w-full text-xs border-collapse">
          <thead className="bg-muted/40 sticky top-0 z-10">
            <tr>
              <th className="px-2 py-1.5 text-start font-medium border-b border-border w-12">#</th>
              {columns.map((c) => (
                <th key={c.fieldKey} className="px-2 py-1.5 text-start font-medium border-b border-border whitespace-nowrap">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleIdx.map((i) => {
              const row = flat[i]
              const v = validations[i]
              return (
                <tr key={i} className={cn('border-b border-border', v.hasBlocking && 'bg-destructive/5')}>
                  <td className="px-2 py-1 text-muted-foreground tabular-nums align-top">{row.displayKey}</td>
                  {columns.map((c) => {
                    const cellId = editKey(row.recordIndex, row.instanceIndex, c.fieldKey)
                    const err = v.blockingErrors[c.fieldKey]
                    const warn = v.softWarnings.find((w) => w.fieldKey === c.fieldKey)
                    const raw = edits[cellId] ?? valueToText(row.inst.fieldValues[c.fieldKey])
                    const isEditing = editing === cellId
                    return (
                      <td
                        key={c.fieldKey}
                        className={cn(
                          'px-1 py-0.5 align-top border-s border-border/50',
                          err && 'bg-destructive/10',
                          !err && warn && 'bg-yellow-500/10',
                        )}
                        title={err?.message ?? warn?.message}
                      >
                        {isEditing ? (
                          <Input
                            autoFocus
                            defaultValue={raw}
                            onBlur={(e) => {
                              onEditCell(row.recordIndex, row.instanceIndex, c.fieldKey, e.target.value)
                              setEditing(null)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                              if (e.key === 'Escape') setEditing(null)
                            }}
                            className="h-7 text-xs"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setEditing(cellId)}
                            className={cn(
                              'w-full text-start px-1 py-0.5 rounded hover:bg-muted/40 min-h-[24px] truncate max-w-[180px] block',
                              err && 'text-destructive',
                            )}
                          >
                            {raw || <span className="text-muted-foreground/50">—</span>}
                          </button>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        روی هر سلول کلیک کنید تا اصلاح شود. خطاها مانع از ورود آن نمونه می‌شوند؛ هشدارها مانع نیستند.
      </p>
    </div>
  )
}

function Stat({ icon: Icon, cls, label }: { icon: typeof CheckCircle2; cls: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <Icon className={cn('w-4 h-4', cls)} />
      {label}
    </span>
  )
}
